import {
  MfaChallengeRequiredFor,
  MfaFactorCreateType,
  ProblemDetail
} from "@wingspanhq/wingspan-api-sdk";

const MFA_REQUIRED_STATUS = 403;

const MFA_REQUIRED_CODE = "StepUpMfaRequired";

export type MfaChallengeRequirement = {
  requiredFor?: MfaChallengeRequiredFor;
  supportedFactorTypes?: MfaFactorCreateType[];
};

export type MfaChallengeHandler = (
  requirement: MfaChallengeRequirement
) => Promise<boolean>;

const toRequiredFor = (value: unknown): MfaChallengeRequiredFor | undefined =>
  typeof value === "string" &&
  (Object.values(MfaChallengeRequiredFor) as string[]).includes(value)
    ? (value as MfaChallengeRequiredFor)
    : undefined;

const toSupportedFactorTypes = (
  value: unknown
): MfaFactorCreateType[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const validTypes = Object.values(MfaFactorCreateType) as string[];
  return value.filter(
    (type): type is MfaFactorCreateType =>
      typeof type === "string" && validTypes.includes(type)
  );
};

class MfaService {
  private handler?: MfaChallengeHandler;
  private inFlight: Promise<boolean> | null = null;

  registerHandler(handler: MfaChallengeHandler): () => void {
    this.handler = handler;
    return () => {
      if (this.handler === handler) {
        this.handler = undefined;
      }
    };
  }

  async getRequirement(
    response: Response
  ): Promise<MfaChallengeRequirement | null> {
    if (response.status !== MFA_REQUIRED_STATUS) {
      return null;
    }

    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      return null;
    }

    if (problem.code !== MFA_REQUIRED_CODE) {
      return null;
    }

    const extensions = problem.extensions ?? {};
    return {
      requiredFor: toRequiredFor(extensions.challengeRequiredFor),
      supportedFactorTypes: toSupportedFactorTypes(
        extensions.supportedFactorTypes
      )
    };
  }

  requestChallenge(requirement: MfaChallengeRequirement): Promise<boolean> {
    if (this.inFlight) {
      return this.inFlight;
    }

    if (!this.handler) {
      return Promise.resolve(false);
    }

    this.inFlight = this.handler(requirement).finally(() => {
      this.inFlight = null;
    });

    return this.inFlight;
  }
}

export const mfaService = new MfaService();
